import { AssetSecurityRight, EquipmentSecurityRight } from '../../security';
import { Asset, AssetKind, AssetTypeEntity } from '../assets';
import { Contact, ContactEntity, ContactType } from '../contact';
import { SitePro } from '../crm';
import { AuditDetails, Entity } from '../entity';
import { EntitySubStatus } from '../entity.status';
import { ActivityEntityName, Address, Attachment } from '../shared';
import { StockLocationEntity } from './stock.location';

export enum EquipmentKind {
    Bulk = 'Bulk',
    Vehicle = 'Vehicle',
    Serialised = 'Serialised',
}

export enum EquipmentUsage {
    Hireable = 'Hireable',
    Internal = "Internal"
}

export interface EquipmentEntity extends Entity {
    kind: EquipmentKind;
    type: Entity;
}

export interface EquipmentCreateRequest {

    kind: EquipmentKind;
    usage: EquipmentUsage;
    type: AssetTypeEntity;

    company: ContactEntity;
    stockLocation: StockLocationEntity;

    name: string;

    make?: string;
    model?: string;

    customFields?: any;
    tags?: Entity[];

    owner?: ContactEntity;

    internalNumber?: string;
}


export interface BulkEquipmentCreateRequest extends EquipmentCreateRequest {
    kind: EquipmentKind.Bulk;
    quantity: number;
}

export interface SerialisedEquipmentCreateRequest extends EquipmentCreateRequest {
    kind: EquipmentKind.Serialised;
    serialNumber: string;
}

export interface VehicleEquipmentCreateRequest extends EquipmentCreateRequest {
    kind: EquipmentKind.Vehicle;
    vin?: string;
    registration?: string;
    registrationExpiry?: Date;
    modelYear?: number;
    // isStockLocation?: boolean;
    // isServiceable?: boolean;
}

export type EquipmentCreateRequests = BulkEquipmentCreateRequest | SerialisedEquipmentCreateRequest | VehicleEquipmentCreateRequest;
export type EquipmentEntities = ActivityEntityName.EquipmentBulk | ActivityEntityName.EquipmentSerialised | ActivityEntityName.EquipmentVehicle;

export type Equipment = Entity & AuditDetails & {

    _id: string;
    kind: EquipmentKind;
    company: ContactEntity;
    owner?: ContactEntity;
    type: AssetTypeEntity;
    equipmentNumber?: string;
    internalNumber?: string;

    image?: Attachment;
    make?: string;
    model?: string;

    customFields?: any;
    tags?: Entity[];

    status?: EntitySubStatus;

    usage: EquipmentUsage;

    isServiceable: boolean;
}

export type BulkEquipment = Equipment & {
    kind: EquipmentKind.Bulk;
}

export type SerialisedEquipment = Equipment & {
    kind: EquipmentKind.Serialised;
    serialNumber: string;
    stockLocation: StockLocationEntity;
}

export type VehicleEquipment = Equipment & {
    kind: EquipmentKind.Vehicle;
    vin: string;
    registration: string;
    registrationExpiry: Date;
    modelYear: number;
    stockLocation: StockLocationEntity;
    isStockLocation: boolean;
    isSchedulable: boolean;
    // lastServiceDate: Date;
    // nextServiceDate: Date;
}

export type AnyEquipment = BulkEquipment | SerialisedEquipment | VehicleEquipment;
export type MovableEquipment = SerialisedEquipment | VehicleEquipment;

export type EquipmentCommandId = 'Create' | 'Transfer' | 'Exists' | 'SerialNumberExists' | 'InternalNumberExists';

export type EquipmentCommands =
    {
        commandId: 'Create',
        data: EquipmentCreateRequest
    } |
    {
        commandId: 'Transfer',
        data: {
            equipmentId: string;
            equipmentIds: string[];
            toStockLocationId: string;
        }
    } |
    {
        commandId: 'Exists',
        data: {
            make: string;
            model: string;
            company: Entity;
        }
    } |
    {
        commandId: 'SerialNumberExists',
        data: {
            make: string;
            model: string;
            serialNumber: string;
        }
    } |
    {
        commandId: 'InternalNumberExists',
        data: {
            internalNumber: string;
        }
    } |
    {
        commandId: 'VinExists',
        data: {
            vin: string;
        }
    } |
    {
        commandId: 'RegistrationExists',
        data: {
            registration: string;
        }
    }

export type ReadonlyEquipment = Pick<SerialisedEquipment, '_id' | 'active' | 'system' | 'color' | 'data' | 'createdAt' | 'updatedAt' | 'kind' | 'equipmentNumber'>;
export type ReadonlyEquipmentKeys = keyof ReadonlyEquipment;
export type PatchableEquipment = Omit<SerialisedEquipment, ReadonlyEquipmentKeys>;
export type PatchableEquipmentKeys = keyof PatchableEquipment;

export const READONLY_EQUIPMENT_KEYS: ReadonlyArray<ReadonlyEquipmentKeys> = [
    '_id',
    'color',
    'data',
    'createdAt',
    'updatedAt',
    'kind',
    'equipmentNumber',
    'system',
    'active'
];

export const PATCHABLE_EQUIPMENT_KEYS: ReadonlyArray<PatchableEquipmentKeys> = [
    'company',
    'createdBy',
    'customFields',
    'image',
    'internalNumber',
    'make',
    'model',
    'name',
    'serialNumber',
    'status',
    'stockLocation',
    'tags',
    'type',
    'updatedBy'
];


export const PATCHABLE_SERIALISED_EQUIPMENT_KEYS: ReadonlyArray<PatchableEquipmentKeys> = [
    'internalNumber',
    'serialNumber',
    'customFields',
    'tags',
    'stockLocation',
    'status',
]

export function equipmentGenerateName(equipment: AnyEquipment): string {

    const { model, make, internalNumber, equipmentNumber, kind, name: equipmentName } = equipment;
    const effectiveNumber = internalNumber ?? equipmentNumber;

    const modeName = [equipmentName, make, model]
        .filter((v) => !!v)
        .map((v) => v?.trim?.())
        .filter((v) => v?.length)
        .join(' ');

    const name = [effectiveNumber, modeName]
        .filter((v) => !!v)
        .map((v) => v?.trim?.())
        .filter((v) => v?.length)
        .join(' - ');

    switch (kind) {

        case EquipmentKind.Bulk:
            // Example: "E12344 - Stanley Hammer Drill"
            return name;

        case EquipmentKind.Serialised:
            // Example: "E1Serialised - Stanley Hammer Drill (12345)"
            const effectiveSerialNumber = equipment.serialNumber?.trim?.() ?? null;
            const serialNumber = effectiveSerialNumber?.length;
            return serialNumber
                ? `${name} (${effectiveSerialNumber})`
                : name;

        default:
            return modeName;
    }

}

export enum SerialisedEquipmentVariant {
    EquipmentLink = 'EquipmentLink',
    EquipmentUnlink = 'EquipmentUnlink',
}

export enum VehicleEquipmentVariant {
    EquipmentLink = 'EquipmentLink',
    EquipmentUnlink = 'EquipmentUnlink',
}

export enum EquipmentBoardVariants {
    Hireable = 'Hireable',
    Internal = 'Internal',
    EmployeeInternal = 'EmployeeInternal',
    EmployeeHireable = 'EmployeeHireable'
}

export const EquipmentEntityAccessRights = Object.freeze({
    [ActivityEntityName.Asset]: [AssetSecurityRight.CanAccessCustomerAssets],
    [ActivityEntityName.EquipmentInternalSerialised]: [AssetSecurityRight.CanAccessCompanyAssets],
    [ActivityEntityName.EquipmentInternalVehicle]: [AssetSecurityRight.CanAccessCompanyAssets],
    [ActivityEntityName.EquipmentBulk]: [EquipmentSecurityRight.CanAccess],
    [ActivityEntityName.EquipmentSerialised]: [EquipmentSecurityRight.CanAccess],
    [ActivityEntityName.EquipmentVehicle]: [EquipmentSecurityRight.CanAccess],
    [ActivityEntityName.StockLocation]: [EquipmentSecurityRight.CanAccess],
});

export const EquipmentEntityManageRights = Object.freeze({
    [ActivityEntityName.Asset]: [AssetSecurityRight.ManageCustomerAssets],
    [ActivityEntityName.EquipmentInternalSerialised]: [AssetSecurityRight.ManageCompanyAssets],
    [ActivityEntityName.EquipmentInternalVehicle]: [AssetSecurityRight.ManageCompanyAssets],
    [ActivityEntityName.EquipmentBulk]: [EquipmentSecurityRight.CanManage],
    [ActivityEntityName.EquipmentSerialised]: [EquipmentSecurityRight.CanManage],
    [ActivityEntityName.EquipmentVehicle]: [EquipmentSecurityRight.CanManage],
    [ActivityEntityName.StockLocation]: [EquipmentSecurityRight.CanManage],
});

export function getContactFromEquipment(vehicle: VehicleEquipment, address: Address): Contact {

    return <Contact>{
        _id: vehicle._id,
        name: vehicle.name,
        type: ContactType.Equipment,
        email: null,
        username: null,
        scheduling: {
            enabled: vehicle.isSchedulable,
            address: address,
        },
        parents: [
            vehicle.company,
            {
                _id: vehicle._id,
                name: vehicle.equipmentNumber
            }
        ],
        active: vehicle.isSchedulable
    };
}

export function getAssetFromEquipment(equipment: AnyEquipment, site: SitePro): Asset {

    let serialNumber: string = null;

    switch (equipment.kind) {
        case EquipmentKind.Serialised:
            serialNumber = (equipment as SerialisedEquipment).serialNumber;
            break;

        case EquipmentKind.Vehicle:
            serialNumber = (equipment as VehicleEquipment).vin;
            break;

        default:
            break;
    }

    return <Asset>{
        _id: equipment._id,
        active: equipment?.isServiceable,
        kind: AssetKind.Equipment,
        name: equipment.name,
        assetType: equipment.type,
        customer: equipment.company,
        site: site,
        customFields: equipment.customFields,
        tags: equipment.tags,
        model: equipment.model,
        serial: serialNumber,
        stockLocation: false,
        allowScheduling: false,
        crew: []
    };
}